import React, {useContext, useEffect, useMemo, useState} from "react";
import TrackVASContext from "../../TrackVASContext";
import {PageTitle} from "../../../_metronic/layout/core";
import {useAuth} from "../../modules/auth";
import {getJobCardBranchStats} from "../generic/crud";
import {CacheTimerBadge} from "../dashboard/CacheTimerBadge";
import {BranchCard} from "./BranchCard";

export function JobCardsPage() {
    const myData = useContext(TrackVASContext);
    const {currentUser} = useAuth();
    const [showAllBranches, setShowAllBranches] = useState(false);

    // Single cached response with branches + stats
    const [branchStatsData, setBranchStatsData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [countdown, setCountdown] = useState(0);
    const [refreshCounter, setRefreshCounter] = useState(0);

    const profileBreadCrumbs = [
        {
            title: "Home",
            path: "/dashboard",
            isSeparator: false,
            isActive: false,
        },
        {
            title: "",
            path: "",
            isSeparator: true,
            isActive: false,
        },
    ];

    useEffect(() => {
        myData.setBaseData({
            toolBarButtons: [],
        });

        return () => {
            myData.setBaseData({
                toolBarButtons: []
            });
        };
    }, []);

    // Fetch branch stats (includes branch list) on load and on refresh
    useEffect(() => {
        fetchBranchStats();
    }, [refreshCounter]);

    // Countdown timer for cache expiry
    useEffect(() => {
        if (!branchStatsData?.cacheExpiry) return;

        const updateCountdown = () => {
            const now = new Date().getTime();
            const expiry = new Date(branchStatsData.cacheExpiry).getTime();
            const remaining = Math.max(0, Math.floor((expiry - now) / 1000));
            setCountdown(remaining);

            if (remaining <= 0) {
                clearInterval(interval);
            }
        };

        updateCountdown();
        const interval = setInterval(updateCountdown, 1000);
        return () => clearInterval(interval);
    }, [branchStatsData?.cacheExpiry]);

    const formatCountdown = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const fetchBranchStats = () => {
        setLoading(true);
        getJobCardBranchStats()
            .then(({data}) => {
                setBranchStatsData(data);
                setLoading(false);
            })
            .catch((error) => {
                console.error("Error fetching branch stats:", error);
                setBranchStatsData(null);
                setLoading(false);
            });
    };

    const handleRefresh = () => {
        setRefreshCounter((prev) => prev + 1);
    };

    // Filter branches client-side based on user role and toggle
    const branches = useMemo(() => {
        const allBranches = branchStatsData?.branches || [];
        const isAdminOrFinance = currentUser?.dashboardType === 'ADMIN_DASHBOARD' ||
                                 currentUser?.dashboardType === 'FINANCE_DASHBOARD';

        // Admin/Finance always see all branches
        if (isAdminOrFinance || showAllBranches) {
            return allBranches;
        }

        // Filter by role
        if (currentUser?.dashboardType === 'MANAGER_DASHBOARD') {
            return allBranches.filter(b => b.managerId === currentUser.userId);
        } else if (currentUser?.dashboardType === 'SUPERVISOR_DASHBOARD') {
            return allBranches.filter(b =>
                b.supervisorId && b.supervisorId.includes(currentUser.userId)
            );
        }

        // Default: filter by supervisor
        return allBranches.filter(b =>
            b.supervisorId && b.supervisorId.includes(currentUser?.userId)
        );
    }, [branchStatsData?.branches, currentUser?.userId, currentUser?.dashboardType, showAllBranches]);

    return (
        <>
            <PageTitle breadcrumbs={profileBreadCrumbs}>Job Cards - Select Branch</PageTitle>
            <div className="card card-body">
                {/* Branch Filter Header */}
                <div className="d-flex flex-wrap align-items-center justify-content-between mb-6 gap-3">
                    <div className="d-flex align-items-center">
                        <i className="bi bi-diagram-3 fs-2 text-primary me-3"></i>
                        <h5 className="fw-bolder text-gray-900 mb-0">
                            {(currentUser.dashboardType === 'ADMIN_DASHBOARD' || currentUser.dashboardType === 'FINANCE_DASHBOARD')
                                ? 'All Branches'
                                : (showAllBranches ? 'All Branches' : 'Your Branches')}
                            {!loading && branches.length > 0 && ` (${branches.length})`}
                        </h5>
                    </div>
                    <div className="d-flex align-items-center gap-2">
                        {branchStatsData && countdown > 0 && (
                            <CacheTimerBadge countdown={countdown} formatCountdown={formatCountdown} />
                        )}
                        <button
                            className="btn btn-sm btn-light-primary"
                            onClick={handleRefresh}
                            disabled={loading}
                        >
                            <i className={`bi bi-arrow-clockwise me-1 ${loading ? 'spinner-border spinner-border-sm' : ''}`}></i>
                            Refresh
                        </button>
                        {/* Only show toggle for Manager/Supervisor users */}
                        {currentUser.dashboardType !== 'ADMIN_DASHBOARD' && currentUser.dashboardType !== 'FINANCE_DASHBOARD' && (
                            <button
                                className={`btn btn-sm ${showAllBranches ? 'btn-primary' : 'btn-light-primary'}`}
                                onClick={() => setShowAllBranches(!showAllBranches)}
                            >
                                <i className={`bi ${showAllBranches ? 'bi-building-check' : 'bi-person-badge'} me-1`}></i>
                                {showAllBranches ? 'All Branches' : 'My Branches'}
                            </button>
                        )}
                    </div>
                </div>

                <div className="separator separator-dashed mb-6"></div>

                {/* Branch Cards - wait for cached data to load */}
                {loading && (
                    <div className="text-center py-10">
                        <span className="spinner-border spinner-border-lg"></span>
                        <p className="mt-3">Loading your branches...</p>
                    </div>
                )}

                {!loading && branches.length === 0 && (
                    <div className="alert alert-light d-flex align-items-center p-5">
                        <i className="bi bi-info-circle fs-2x text-primary me-4"></i>
                        <div className="d-flex flex-column">
                            <h5 className="mb-1">No branches found</h5>
                            <span>You are not assigned as manager or supervisor to any branches.</span>
                        </div>
                    </div>
                )}

                {!loading && branches.length > 0 && (
                    <div className="row">
                        {branches.map((branch) => (
                            <BranchCard
                                key={branch.id}
                                branch={branch}
                                preloadedStats={branchStatsData?.branchStats?.[branch.id] || {
                                    activeJobCardCount: 0,
                                    totalTreatments: 0,
                                    completedTreatments: 0,
                                    treatmentProgress: 0,
                                    closedLast7Days: 0,
                                    todayAttendanceCount: 0
                                }}
                            />
                        ))}
                    </div>
                )}
            </div>
        </>
    );
}
